import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useApi } from '../../hooks/useApi';
import { hapticFeedback } from '../../utils/telegram';
import './Licenses.css';
import Notifications from '../common/Notifications/Notifications';
import Settings from '../common/Settings/Settings';
import { useUserProfile } from '../../hooks/useUserProfile';
import FormattedNumber from '../common/FormattedNumber/FormattedNumber';
import CountdownTimer from '../common/CountdownTimer/CountdownTimer';
import { formatNumber } from '../../utils/numUtils';

const LICENSE_DATA = {
  FREE: {
    tradesPerDay: 1,
    maxTradeVolume: 50,
    price: 0
  },
  BASIC: {
    tradesPerDay: 3,
    maxTradeVolume: 500,
    price: 150
  },
  STANDART: {
    tradesPerDay: 7,
    maxTradeVolume: 2500,
    price: 1250
  },
  PRO: {
    tradesPerDay: 12,
    maxTradeVolume: 5000,
    price: 3000
  },
  PREMIUM: {
    tradesPerDay: 18,
    maxTradeVolume: 10000,
    price: 7500
  },
  VIP: {
    tradesPerDay: 500,
    maxTradeVolume: 1_000_000,
    price: 50_000
  }
};

const Licenses = () => {
  const { user, refreshUserData } = useUser();
  const { t } = useLanguage();
  const { createDeposit, requestWithdrawal, purchaseLicense, getPricingInfo } = useApi();
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [showPurchaseConfirmation, setShowPurchaseConfirmation] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);
  const { displayName, photo } = useUserProfile();

  const currentLicenseType = user?.licenseType || 'FREE';
  const availableTrades = user?.availableDeals || 0;
  const userBalance = user?.balance || 0;

  // Load pricing information on component mount
  useEffect(() => {
    const loadPricingInfo = async () => {
      try {
        setIsLoadingPrices(true);
        const data = await getPricingInfo();
        setPricingInfo(data);
      } catch (error) {
        console.error('Failed to load pricing info:', error);
      } finally {
        setIsLoadingPrices(false);
      }
    };

    loadPricingInfo();
  }, []);

  const handlePurchaseClick = (licenseType) => {
    hapticFeedback.medium();
    setSelectedLicense(licenseType);
    setShowPurchaseConfirmation(true);
    setPurchaseError(null);
  };

  const confirmPurchase = async () => {
    if (!selectedLicense) return;

    try {
      setIsProcessing(true);
      setPurchaseError(null);
      hapticFeedback.medium();

      // Call the API endpoint to purchase the license using the hook
      const data = await purchaseLicense(selectedLicense);

      // If successful, refresh user data to get updated license info
      await refreshUserData();
      hapticFeedback.success();

      // Close the confirmation modal
      setShowPurchaseConfirmation(false);
      setSelectedLicense(null);

    } catch (error) {
      console.error('Error purchasing license:', error);
      hapticFeedback.error();
      setPurchaseError(error.message || t('failedToPurchaseLicense'));
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelPurchase = () => {
    hapticFeedback.light();
    setShowPurchaseConfirmation(false);
    setSelectedLicense(null);
    setPurchaseError(null);
  };

  // Function to get current price for a license type
  const getCurrentPrice = (licenseType) => {
    if (!pricingInfo) return LICENSE_DATA[licenseType]?.price || 0;
    return pricingInfo.currentPrices?.[licenseType] || LICENSE_DATA[licenseType]?.price || 0;
  };

  // Function to get future price for a license type
  const getFuturePrice = (licenseType) => {
    if (!pricingInfo?.futurePrices) return null;
    return pricingInfo.futurePrices[licenseType];
  };

  // Function to check if user has enough balance for a license
  const hasEnoughBalance = (licensePrice) => {
    return userBalance >= licensePrice;
  };

  return (
    <div className="licenses-page">
      <div className="user-profile-card">
        <div className="user-profile-header">
          <div className="user-avatar-container">
            <img src={photo} alt="User avatar" className="user-avatar" />
          </div>
          <div className="user-name">
            {displayName || "Alex_245"}
          </div>
          <div className="user-controls">
            <Notifications />
            <Settings />
          </div>
        </div>

        <div className="current-license-info">
          <div className="license-info-row">
            <div className="license-info-label">{t('currentLicenseLabel')}</div>
            <div className="license-info-value">{currentLicenseType}</div>
          </div>
          <div className="license-info-row">
            <div className="license-info-label">{t('numberOfDealsLabel')}</div>
            <div className="license-info-value">{LICENSE_DATA[currentLicenseType]?.tradesPerDay || 1} {t('perDay')}</div>
          </div>
          <div className="license-info-row">
            <div className="license-info-label">{t('availableDeals')}</div>
            <div className="license-info-value">{availableTrades}</div>
          </div>
          <div className="license-info-row">
            <div className="license-info-label">{t('balance')}</div>
            <FormattedNumber value={userBalance} suffix="USDT" className="license-info-value" />
          </div>
        </div>
      </div>

      <h2 className="section-title">{t('licensesAndFeaturesTitle')}</h2>

      {/* Price increase countdown timer */}
      {/* {pricingInfo?.priceIncreaseInfo && (
        <CountdownTimer
          targetDate={pricingInfo.priceIncreaseInfo.priceIncreaseDate}
          onExpire={() => {
            // Refresh pricing info when timer expires
            // getPricingInfo().then(setPricingInfo).catch(console.error);
          }}
        />
      )} */}

      <div className="license-cards">
        {isLoadingPrices ? (
          <div className="loading-prices">
            <div className="loading-spinner"></div>
            <p>Загрузка актуальных цен...</p>
          </div>
        ) : (
          Object.entries(LICENSE_DATA).map(([type, data]) => {
          if (type === 'FREE') return null;

          const currentPrice = getCurrentPrice(type);
          const futurePrice = getFuturePrice(type);
          const isCurrentLicense = type === currentLicenseType;
          const isUpgrade = currentPrice > (getCurrentPrice(currentLicenseType) || 0);
          const canAfford = hasEnoughBalance(currentPrice);

          // Disable if current license, not an upgrade, or insufficient balance
          const isDisabled = isCurrentLicense || !isUpgrade || !canAfford;

          // Show price increase info
          const showPriceIncrease = futurePrice && futurePrice > currentPrice && !pricingInfo?.priceIncreaseInfo?.isActive;

          return (
            <div key={type} className="license-card">
              <div className="license-card-header">
                <div className="license-card-type">{type}</div>
                <div className="license-card-price-container">
                  <div className="license-card-price">{formatNumber(currentPrice)} USDT</div>
                  {showPriceIncrease && (
                    <div className="license-card-future-price">
                      {formatNumber(futurePrice)} USDT
                    </div>
                  )}
                </div>
              </div>

              <div className="license-card-details">
                <div className="license-card-detail-row">
                  <div className="license-card-detail-label">{t('numberOfDealsLabel')}</div>
                  <div className="license-card-detail-value">{data.tradesPerDay} {t('perDay')}</div>
                </div>
                <div className="license-card-detail-row">
                  <div className="license-card-detail-label">{t('maxTradeVolumeLabel')}</div>
                  <div className="license-card-detail-value">{t('maxTradeVolumeValue', { amount: formatNumber(data.maxTradeVolume) })}</div>
                </div>
              </div>

              <button
                className={`buy-license-button ${isDisabled ? 'disabled' : ''}`}
                disabled={isDisabled}
                onClick={() => {
                  if (!isDisabled) {
                    hapticFeedback.medium();
                  } else {
                    hapticFeedback.light();
                  }
                  handlePurchaseClick(type);
                }}
              >
                <>
                  {currentLicenseType === type && (
                    <div className="license-card-detail-value">{t('purchasedLicense')}</div>
                  )}
                  {currentLicenseType !== type && (
                    <>
                      {!canAfford ? t('insufficientFundsButton') : t('buyLicense')}
                    </>
                  )}
                  {!canAfford && (
                    <div className="insufficient-balance-note">
                      {t('needToTopUpBalance', { amount: formatNumber(currentPrice - userBalance) })}
                    </div>
                  )}
                </>
              </button>

            </div>
                      );
          })
        )}
      </div>

      {showPurchaseConfirmation && (
        <div className="purchase-confirmation-modal">
          <div className="purchase-confirmation-content">
            <h3 className="purchase-confirmation-title">{t('purchaseConfirmationTitle')}</h3>
            <p className="purchase-confirmation-message">
              {t('purchaseConfirmationMessage', { 
                license: selectedLicense, 
                price: getCurrentPrice(selectedLicense)
              })}
            </p>

            {purchaseError && (
              <div className="purchase-error-message">
                {purchaseError}
              </div>
            )}

            <div className="purchase-confirmation-actions">
              <button
                className="cancel-purchase-button"
                onClick={() => {
                  hapticFeedback.light();
                  cancelPurchase();
                }}
                disabled={isProcessing}
              >
                {t('cancel')}
              </button>
              <button
                className="confirm-purchase-button"
                onClick={() => {
                  if (!isProcessing) {
                    hapticFeedback.medium();
                  }
                  confirmPurchase();
                }}
                disabled={isProcessing}
              >
                {isProcessing ? t('processing') : t('confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Licenses; 